// status-operating-table-text.ts
//
// Split out of status-operating-table.ts (06g R5 — that file reached the
// 200-line budget once the cost line landed). Owns the plain-text table
// `avatar autopilot status` prints (non-`--json`, non-HTML): one line per
// ticket, the mission line, and — 06g R5 — `chi phí: $X.XX / trần $Y (Z %)`
// right under it. No I/O, no ANSI.
import {
  formatAttemptCell,
  formatMissionStatusLine,
  formatPendingGateRequests,
  type MissionOperatingSummary,
  type TicketOperatingRow,
} from "./status-operating-table.js";

/** 06g R5: `\nchi phí: $X.XX / trần $Y (Z %)` — empty when the summary carries no ceiling (pre-R5 callers). */
export function formatMissionCostLine(summary: MissionOperatingSummary): string {
  if (summary.costLimitUsd === undefined) return "";
  if (summary.costUsd === undefined)
    return `\nchi phí: chưa có usage / trần $${summary.costLimitUsd}`;
  const percent = Math.round((summary.costUsd / summary.costLimitUsd) * 100);
  return `\nchi phí: $${summary.costUsd.toFixed(2)} / trần $${summary.costLimitUsd} (${percent} %)`;
}

function padRight(value: string, width: number): string {
  return value.length >= width ? value : value + " ".repeat(width - value.length);
}

/**
 * Compact terminal table for `avatar autopilot status` (non-`--json`,
 * non-HTML output) — one line per ticket, column-aligned, plus a mission
 * summary line underneath (spec §15: "in bảng gọn ... dưới bảng là dòng
 * mission"). No color/ANSI — terminal-logger.ts's `log.info` handles that
 * layer; this function only returns plain text lines.
 */
export function renderStatusTableText(
  rows: TicketOperatingRow[],
  summary: MissionOperatingSummary,
): string {
  const missionLine =
    formatMissionStatusLine(summary.missionId, summary.status) +
    (summary.pendingGates.length > 0
      ? ` · gate chờ: ${formatPendingGateRequests(summary.pendingGates)}`
      : "") +
    (summary.quotaWakeAt !== undefined ? ` · quota wake_at: ${summary.quotaWakeAt}` : "");
  // Phase 05 Req 8: shown right under the mission line whenever cost is
  // missing figures for one or more counted attempts (killed/SIGINT'd/
  // unlocked before ever emitting attempt_usage_recorded).
  const costCaveat =
    formatMissionCostLine(summary) +
    (summary.attemptsWithoutUsage > 0
      ? `\nchi phí chưa gồm ${summary.attemptsWithoutUsage} attempt không có usage (bị ngắt hoặc đang chạy)`
      : "") +
    (summary.decisionPolicyWarning !== undefined
      ? `\n⚠ decision_policy: ${summary.decisionPolicyWarning}`
      : "");

  if (rows.length === 0) {
    return `${missionLine} — chưa có ticket nào.${costCaveat}`;
  }

  const idWidth = Math.max(2, ...rows.map((r) => r.id.length));
  const kindWidth = Math.max(4, ...rows.map((r) => r.kind.length));
  const sliceWidth = Math.max(5, ...rows.map((r) => r.sliceId.length));
  const statusWidth = Math.max(6, ...rows.map((r) => r.status.length));

  const lines = rows.map((r) => {
    const attempt = formatAttemptCell(r);
    const verdict =
      r.lastVerdictCategory !== undefined
        ? `${r.lastVerdictCategory}: ${r.lastVerdictReasons.join("; ")}`
        : "";
    return (
      `${padRight(r.id, idWidth)}  ${padRight(r.kind, kindWidth)}  ` +
      `${padRight(r.sliceId, sliceWidth)}  ${padRight(r.status, statusWidth)}  ` +
      `${padRight(attempt, 16)}  ${verdict}`
    ).trimEnd();
  });

  return [...lines, missionLine].join("\n") + costCaveat;
}
